import { Stack, TextField, Tooltip, Typography } from "@mui/material";

interface NGSpacingInputProps {
  /**
   * Label displayed before the input (e.g. "M" for margin or "P" for padding)
   */
  label?: string;
  /**
   * Current spacing value (e.g. "10px 5px")
   */
  value: string;

  /**
   * Optional title for the input field shown as tooltip when hovering
   */
  title?: string;
  /**
   * Called when the value changes
   */
  onChange: (newValue: string) => void;
}

// Basic unit regex: either “0” or number (int or decimal) + px|em|rem|%
const UNIT_REGEX = /^(?:0|-?\d+(?:\.\d+)?(?:px|em|rem|%))$/;

// CSS variable regex: matches patterns like `var(--name)` or `var(--name, fallback)`
const CSS_VARIABLE_REGEX = /^var\(\s*([^,\s)]+)\s*(?:,\s*(.+?)\s*)?\)$/;

/**
 * Validate a CSS spacing string: one to four tokens, each must match UNIT_REGEX.
 * Supports "!important" at the end.
 */
function isValidSpacing(value: string): boolean {
  try {
    if (!value) return true;
    if (typeof value === "object") {
      // If value is an object, we assume it's a responsive spacing object
      return Object.values(value).every((val) => isValidSpacing(val as string));
    }

    // Remove trailing "!important"
    const cleanValue = value.toString().trim().replace(/\s*!important$/, "");

    const tokens = cleanValue.trim().split(/\s+/);
    if (tokens.length > 4) return false;

    // Allow CSS variable syntax
    if (tokens.length === 1 && CSS_VARIABLE_REGEX.test(tokens[0])) return true;

    return tokens.every((token) => UNIT_REGEX.test(token));
  } catch (error) {
    console.log("Error validating spacing:", { error });
    return false;
  }
}

/**
 * Reusable component for editing margin/padding values with simple validation.
 */
export function NGSpacingInput({ label, title, value, onChange }: NGSpacingInputProps) {
  // variables
  const hasLabel = Boolean(label);
  const isValid = isValidSpacing(value);
  // helpers
  function describeSpacing(value: string | Record<string, string>): string {
    if (typeof value === "object" && value !== null) {
      const answer = Object.entries(value)
        .map(([breakpoint, val]) => {
          return `${breakpoint}: ${val}`;
        })
        .join(", ");

      return answer;
    }
    const tokens = value.toString().trim().split(/\s+/);

    if (tokens.length === 0 || !value.toString().trim()) return "Top, Right, Bottom, Left";
    if (tokens.length === 1) return `All sides: ${tokens[0]}`;
    if (tokens.length === 2) return `Top & Bottom: ${tokens[0]}, Left & Right: ${tokens[1]}`;
    if (tokens.length === 3) return `Top: ${tokens[0]}, Left & Right: ${tokens[1]}, Bottom: ${tokens[2]}`;
    if (tokens.length === 4) return `Top: ${tokens[0]}, Right: ${tokens[1]}, Bottom: ${tokens[2]}, Left: ${tokens[3]}`;

    return "";
  }

  /**
   * Formats spacing description into individually wrapped chunks that don't break mid-label,
   * making the label (including the colon) bold and leaving the value unbolded.
   */
  function renderFormattedHelper(text: string) {
    if (!text.includes(":")) return text;

    return text.split(/, /).map((chunk, index, arr) => {
      // Split into “label:” and the “value”
      const [labelPart, valuePart] = chunk.split(/: (.+)/);
      return (
        <span
          key={index}
          style={{
            display: "inline-block",
            marginRight: index < arr.length - 1 ? 4 : 0,
          }}
        >
          <strong>{labelPart}:</strong> {valuePart}
          {index < arr.length - 1 && ","}
        </span>
      );
    });
  }

  // variables
  const helperText = isValid ? <>{renderFormattedHelper(describeSpacing(value))}</> : "Invalid spacing";
  // render
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      gap={hasLabel ? "0.1rem" : 0}
      flex={1}
      padding={hasLabel ? 0 : "0 0.2rem"}
    >
      {hasLabel && (
        <Tooltip title={title} placement="top">
          <Typography paddingLeft="3px" className="control-label" marginTop="1rem" marginRight="0.1rem">
            {label}
          </Typography>
        </Tooltip>
      )}

      <TextField
        variant="outlined"
        size="small"
        fullWidth
        value={typeof value === "string" ? value : JSON.stringify(value)}
        onBlur={(e) => onChange(e.target.value.trim())}
        onChange={(e) => onChange(e.target.value)}
        error={!isValid}
        helperText={helperText}
        placeholder="e.g. 10px 5px 5px 3px"
        sx={{ marginTop: 0 }}
      />
    </Stack>
  );
}
